import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  OpsFinanceCostCadence,
  OpsFinanceCurrency,
  OpsStoredFxSource,
  type OpsFinancePlanLine,
} from '../../../../libs/api-client/src/generated/models';
import { Icon } from '../components/Brand';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageBody, PageHead } from '../components/Page';
import { Badge } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { opsErrorMessage } from '../lib/ops-api';
import {
  COST_CATEGORIES,
  OPERATIONS_PATH,
  PLAN_LINES,
  costDraftToInput,
  costOverlaps,
  dayOfDate,
  emptyCostDraft,
  formatMoney,
  formatMonthShort,
  isMonthKey,
  monthOfDate,
  parseAmount,
  validateCostDraft,
  type CostDraft,
  type CostDraftErrors,
} from '../lib/operations';
import {
  OPERATIONS_QUERY_KEYS,
  clearFxRate,
  createCashBalance,
  createCostItem,
  deleteCashBalance,
  deleteCostItem,
  fetchBudgets,
  fetchCashBalances,
  fetchCostItems,
  fetchFxRates,
  fetchPartnerStatus,
  setBudget,
  setFxRate,
  startPartnerSync,
  updateCostItem,
  type OpsCostItem,
} from '../lib/operations-api';

/** The tabs, in order. `?tab=` deep-links one (the report links to `plan` and `partner`). */
const TABS = ['costs', 'plan', 'cash', 'fx', 'partner'] as const;
type Tab = (typeof TABS)[number];

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t === value);
}

/**
 * The operations dashboard's settings sub-page (`/operations/settings`) — no sidebar entry of its
 * own; the dashboard's header button and its `?tab=` deep links lead here, and the sidebar keeps
 * "Operations-Dashboard" highlighted while it is open.
 *
 * What the team maintains for the operations dashboard: the cost ledger, monthly plan figures,
 * cash balances, USD→EUR rate overrides, and the Shopify Partner API mirror's status.
 *
 * Every write lands in the audit log with who and what (a generated reason — these are our own
 * management figures, not merchant data).
 */
export function OperationsInputsPage() {
  const { t } = useLocale();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: Tab = isTab(requested) ? requested : 'costs';

  return (
    <>
      <PageHead
        eyebrow={t('operations.inputs.eyebrow')}
        title={t('operations.inputs.title')}
        lead={t('operations.inputs.lead')}
        actions={
          <Link to={OPERATIONS_PATH} className="btn-secondary btn-sm gap-2">
            <Icon name="arrowLeft" className="h-4 w-4" />
            {t('operations.inputs.toReport')}
          </Link>
        }
      >
        <div className="tabs w-fit flex-wrap" role="tablist">
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`tab ${tab === id ? 'tab-active' : 'tab-idle'}`}
              onClick={() => setParams({ tab: id })}
            >
              {t(`operations.inputs.tabs.${id}`)}
            </button>
          ))}
        </div>
      </PageHead>
      <PageBody>
        {tab === 'costs' && <CostsTab />}
        {tab === 'plan' && <PlanTab />}
        {tab === 'cash' && <CashTab />}
        {tab === 'fx' && <FxTab />}
        {tab === 'partner' && <PartnerTab />}
      </PageBody>
    </>
  );
}

/** Invalidates the report plus the given input list. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return (key: readonly string[]) => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({
      queryKey: OPERATIONS_QUERY_KEYS.report,
    });
  };
}

// ── Costs ───────────────────────────────────────────────────────────────────

function draftFrom(item: OpsCostItem): CostDraft {
  return {
    name: item.name,
    category: item.category,
    cadence: item.cadence,
    amount: String(item.amount),
    currency: item.currency,
    startMonth: item.startMonth,
    endMonth: item.endMonth ?? '',
    vendor: item.vendor,
    note: item.note,
  };
}

function CostsTab() {
  const { t, locale } = useLocale();
  const invalidate = useInvalidate();
  const current = monthOfDate(new Date());
  const costs = useQuery({
    queryKey: OPERATIONS_QUERY_KEYS.costs,
    queryFn: fetchCostItems,
  });
  const [editing, setEditing] = useState<OpsCostItem | null>(null);
  const [deleting, setDeleting] = useState<OpsCostItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const end = useMutation({
    mutationFn: (item: OpsCostItem) =>
      updateCostItem(
        item.id,
        costDraftToInput({ ...draftFrom(item), endMonth: current }),
      ),
    onSuccess: (item) => {
      setNotice(
        t('operations.costs.ended', {
          name: item.name,
          month: formatMonthShort(current, locale),
        }),
      );
      invalidate(OPERATIONS_QUERY_KEYS.costs);
    },
  });
  const remove = useMutation({
    mutationFn: (item: OpsCostItem) => deleteCostItem(item.id),
    onSuccess: () => {
      setDeleting(null);
      setNotice(t('operations.costs.deleted'));
      invalidate(OPERATIONS_QUERY_KEYS.costs);
    },
  });

  const running = (costs.data?.data ?? []).filter((c) => c.runningNow);
  const overlaps = costOverlaps(costs.data?.data ?? []);
  const monthlyEur = running
    .filter((c) => c.currency === OpsFinanceCurrency.Eur)
    .reduce((sum, c) => sum + c.monthlyEquivalent, 0);

  return (
    <div className="space-y-5">
      <StatusBanner tone="info">
        <ul className="list-disc pl-5 space-y-1">
          <li>{t('operations.costs.factNet')}</li>
          <li>{t('operations.costs.factYearly')}</li>
          <li>{t('operations.costs.factEnd')}</li>
        </ul>
      </StatusBanner>
      {notice && <StatusBanner tone="success">{notice}</StatusBanner>}
      {(end.isError || remove.isError) && (
        <StatusBanner tone="error">
          {opsErrorMessage(
            end.error ?? remove.error,
            t('operations.saveFailed'),
          )}
        </StatusBanner>
      )}

      <CostForm
        key={editing?.id ?? 'new'}
        editing={editing}
        current={current}
        onDone={(message) => {
          setEditing(null);
          setNotice(message);
          invalidate(OPERATIONS_QUERY_KEYS.costs);
        }}
        onCancel={() => setEditing(null)}
      />

      <section className="surface p-5 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900">
            {t('operations.costs.listTitle')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('operations.costs.runningTotal', {
              amount: formatMoney(monthlyEur, locale, { cents: true }),
            })}
          </p>
        </div>
        {costs.isLoading && (
          <StatusBanner tone="loading">{t('operations.loading')}</StatusBanner>
        )}
        {costs.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(costs.error, t('operations.loadFailed'))}
          </StatusBanner>
        )}
        {costs.data && costs.data.data.length === 0 && (
          <p className="text-sm text-gray-500">{t('operations.costs.empty')}</p>
        )}
        {costs.data && costs.data.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('operations.costs.name')}</th>
                  <th>{t('operations.costs.category')}</th>
                  <th className="text-right">{t('operations.costs.amount')}</th>
                  <th className="text-right">
                    {t('operations.costs.perMonth')}
                  </th>
                  <th>{t('operations.costs.window')}</th>
                  <th>{t('operations.costs.changedBy')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {costs.data.data.map((item) => (
                  <tr
                    key={item.id}
                    className={item.runningNow ? '' : 'text-gray-400'}
                  >
                    <td className="py-2">
                      <div className="font-medium text-gray-900">
                        {item.name}
                      </div>
                      {overlaps.has(item.id) && (
                        <div className="mt-1">
                          <Badge tone="amber">
                            {t('operations.costs.overlap', {
                              month: formatMonthShort(
                                overlaps.get(item.id) ?? item.startMonth,
                                locale,
                              ),
                            })}
                          </Badge>
                        </div>
                      )}
                      {(item.vendor || item.note) && (
                        <div
                          className="text-xs text-gray-500 max-w-xs truncate"
                          title={item.note}
                        >
                          {[item.vendor, item.note].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{t(`operations.category.${item.category}`)}</div>
                      <div className="text-xs text-gray-500">
                        {t(`operations.section.${item.section}`)}
                      </div>
                    </td>
                    <td className="text-right tabular-nums">
                      {formatMoney(item.amount, locale, {
                        currency: item.currency,
                        cents: true,
                      })}
                      <div className="text-xs text-gray-500">
                        {t(`operations.cadence.${item.cadence}`)}
                      </div>
                    </td>
                    <td className="text-right tabular-nums">
                      {item.cadence === OpsFinanceCostCadence.OneOff
                        ? '—'
                        : formatMoney(item.monthlyEquivalent, locale, {
                            currency: item.currency,
                            cents: true,
                          })}
                    </td>
                    <td className="whitespace-nowrap">
                      {item.cadence === OpsFinanceCostCadence.OneOff
                        ? formatMonthShort(item.startMonth, locale)
                        : `${formatMonthShort(item.startMonth, locale)} – ${
                            item.endMonth
                              ? formatMonthShort(item.endMonth, locale)
                              : t('operations.costs.open')
                          }`}
                      {item.runningNow && (
                        <span className="ml-2">
                          <Badge tone="teal">
                            {t('operations.costs.running')}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-gray-500">{item.updatedBy}</td>
                    <td className="whitespace-nowrap text-right space-x-1">
                      <button
                        type="button"
                        className="btn-neutral btn-sm"
                        onClick={() => setEditing(item)}
                      >
                        {t('operations.costs.edit')}
                      </button>
                      {item.runningNow &&
                        item.cadence !== OpsFinanceCostCadence.OneOff && (
                          <button
                            type="button"
                            className="btn-neutral btn-sm"
                            disabled={end.isPending}
                            onClick={() => end.mutate(item)}
                            title={t('operations.costs.endHint')}
                          >
                            {t('operations.costs.end')}
                          </button>
                        )}
                      <button
                        type="button"
                        className="btn-neutral btn-sm"
                        onClick={() => setDeleting(item)}
                      >
                        {t('operations.costs.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={deleting !== null}
        title={t('operations.costs.deleteTitle')}
        confirmLabel={t('operations.costs.delete')}
        confirmDisabled={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      >
        <p>
          {t('operations.costs.deleteBody', { name: deleting?.name ?? '' })}
        </p>
      </ConfirmDialog>
    </div>
  );
}

function CostForm({
  editing,
  current,
  onDone,
  onCancel,
}: {
  readonly editing: OpsCostItem | null;
  readonly current: string;
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}) {
  const { t, locale } = useLocale();
  const [draft, setDraft] = useState<CostDraft>(() =>
    editing ? draftFrom(editing) : emptyCostDraft(current),
  );
  const [errors, setErrors] = useState<CostDraftErrors>({});

  const save = useMutation({
    mutationFn: (d: CostDraft) =>
      editing
        ? updateCostItem(editing.id, costDraftToInput(d))
        : createCostItem(costDraftToInput(d)),
    onSuccess: (item) => {
      if (!editing) setDraft(emptyCostDraft(current));
      onDone(
        t(editing ? 'operations.costs.updated' : 'operations.costs.created', {
          name: item.name,
        }),
      );
    },
  });

  function set<K extends keyof CostDraft>(key: K, value: CostDraft[K]): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    const found = validateCostDraft(draft);
    setErrors(found);
    if (Object.keys(found).length === 0) save.mutate(draft);
  }

  const parsed = parseAmount(draft.amount);
  const preview =
    parsed !== null && draft.cadence === OpsFinanceCostCadence.Yearly
      ? t('operations.costs.yearlyPreview', {
          amount: formatMoney(parsed / 12, locale, {
            currency: draft.currency,
            cents: true,
          }),
        })
      : null;

  return (
    <section className="card space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {editing
            ? t('operations.costs.editTitle', { name: editing.name })
            : t('operations.costs.formTitle')}
        </h2>
        {editing && (
          <p className="text-sm text-amber-700">
            {t('operations.costs.editWarning')}
          </p>
        )}
      </div>
      {save.isError && (
        <StatusBanner tone="error">
          {opsErrorMessage(save.error, t('operations.saveFailed'))}
        </StatusBanner>
      )}
      <form onSubmit={submit} className="grid gap-4 md:grid-cols-3">
        <Field
          id="costName"
          label={t('operations.costs.name')}
          error={errors.name}
        >
          <input
            id="costName"
            className="field-input"
            value={draft.name}
            maxLength={120}
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field id="costCategory" label={t('operations.costs.category')}>
          <select
            id="costCategory"
            className="field-input"
            value={draft.category}
            onChange={(e) => {
              const hit = COST_CATEGORIES.find((c) => c === e.target.value);
              if (hit) set('category', hit);
            }}
          >
            {COST_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`operations.category.${c}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field id="costCadence" label={t('operations.costs.cadence')}>
          <select
            id="costCadence"
            className="field-input"
            value={draft.cadence}
            onChange={(e) => {
              const hit = Object.values(OpsFinanceCostCadence).find(
                (c) => c === e.target.value,
              );
              if (hit) set('cadence', hit);
            }}
          >
            {Object.values(OpsFinanceCostCadence).map((c) => (
              <option key={c} value={c}>
                {t(`operations.cadence.${c}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="costAmount"
          label={t('operations.costs.amountNet')}
          error={errors.amount}
          hint={preview ?? t('operations.costs.amountHint')}
        >
          <div className="flex gap-2">
            <input
              id="costAmount"
              className="field-input"
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => set('amount', e.target.value)}
            />
            <select
              aria-label={t('operations.costs.currency')}
              className="field-input w-24"
              value={draft.currency}
              onChange={(e) =>
                set(
                  'currency',
                  e.target.value === OpsFinanceCurrency.Usd
                    ? OpsFinanceCurrency.Usd
                    : OpsFinanceCurrency.Eur,
                )
              }
            >
              <option value={OpsFinanceCurrency.Eur}>EUR</option>
              <option value={OpsFinanceCurrency.Usd}>USD</option>
            </select>
          </div>
        </Field>
        <Field
          id="costStart"
          label={
            draft.cadence === OpsFinanceCostCadence.OneOff
              ? t('operations.costs.bookedIn')
              : t('operations.costs.startMonth')
          }
          error={errors.startMonth}
        >
          <input
            id="costStart"
            type="month"
            className="field-input"
            value={draft.startMonth}
            onChange={(e) => set('startMonth', e.target.value)}
          />
        </Field>
        {draft.cadence !== OpsFinanceCostCadence.OneOff && (
          <Field
            id="costEnd"
            label={t('operations.costs.endMonth')}
            error={errors.endMonth}
            hint={t('operations.costs.endMonthHint')}
          >
            <input
              id="costEnd"
              type="month"
              className="field-input"
              value={draft.endMonth}
              onChange={(e) => set('endMonth', e.target.value)}
            />
          </Field>
        )}
        <Field id="costVendor" label={t('operations.costs.vendor')}>
          <input
            id="costVendor"
            className="field-input"
            value={draft.vendor}
            maxLength={120}
            onChange={(e) => set('vendor', e.target.value)}
          />
        </Field>
        <div className="md:col-span-2">
          <Field
            id="costNote"
            label={t('operations.costs.note')}
            hint={t('operations.costs.noteHint')}
          >
            <input
              id="costNote"
              className="field-input"
              value={draft.note}
              maxLength={1000}
              onChange={(e) => set('note', e.target.value)}
            />
          </Field>
        </div>
        <div className="md:col-span-3 flex gap-2">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={save.isPending}
          >
            {editing
              ? t('operations.costs.saveEdit')
              : t('operations.costs.add')}
          </button>
          {editing && (
            <button
              type="button"
              className="btn-neutral btn-sm"
              onClick={onCancel}
            >
              {t('operations.cancel')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly error?: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}) {
  const { t } = useLocale();
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children}
      {error ? (
        <p className="field-error">{t(error)}</p>
      ) : hint ? (
        <p className="field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

// ── Plan ────────────────────────────────────────────────────────────────────

function PlanTab() {
  const { t, locale } = useLocale();
  const invalidate = useInvalidate();
  const [year, setYear] = useState(() =>
    Number(monthOfDate(new Date()).slice(0, 4)),
  );
  const budgets = useQuery({
    queryKey: [...OPERATIONS_QUERY_KEYS.budgets, year],
    queryFn: () => fetchBudgets(year),
  });
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const months = Array.from(
    { length: 12 },
    (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`,
  );

  const save = useMutation({
    mutationFn: setBudget,
    onSuccess: () => invalidate(OPERATIONS_QUERY_KEYS.budgets),
    onError: (e) => setError(opsErrorMessage(e, t('operations.saveFailed'))),
  });

  const stored = (month: string, line: string): number | undefined =>
    budgets.data?.data.find((b) => b.month === month && b.line === line)
      ?.amountEur;

  function commit(month: string, line: OpsFinancePlanLine): void {
    const key = `${month}|${line}`;
    const raw = edits[key];
    if (raw === undefined) return;
    setEdits((s) => {
      const next = { ...s };
      delete next[key];
      return next;
    });
    const before = stored(month, line);
    if (raw.trim() === '') {
      if (before !== undefined) save.mutate({ month, line, amountEur: null });
      return;
    }
    const amount = parseAmount(raw);
    if (amount === null || amount < 0) {
      setError(t('operations.plan.invalid', { value: raw }));
      return;
    }
    if (amount !== before) save.mutate({ month, line, amountEur: amount });
  }

  return (
    <div className="space-y-5">
      <StatusBanner tone="info">{t('operations.plan.inputLead')}</StatusBanner>
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      <section className="surface p-5 space-y-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-neutral btn-sm"
            onClick={() => setYear((y) => y - 1)}
            aria-label={t('operations.plan.prevYear')}
          >
            ‹
          </button>
          <span className="text-base font-semibold text-gray-900 tabular-nums">
            {year}
          </span>
          <button
            type="button"
            className="btn-neutral btn-sm"
            onClick={() => setYear((y) => y + 1)}
            aria-label={t('operations.plan.nextYear')}
          >
            ›
          </button>
          {save.isPending && (
            <span className="text-xs text-gray-500">
              {t('operations.saving')}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="data-table min-w-max">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white pr-4">
                  {t('operations.plan.line')}
                </th>
                {months.map((m) => (
                  <th key={m} className="text-right px-1">
                    {formatMonthShort(m, locale)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_LINES.map((line) => (
                <tr key={line}>
                  <td className="sticky left-0 bg-white pr-4 py-1 whitespace-nowrap">
                    {t(`operations.planLine.${line}`)}
                  </td>
                  {months.map((month) => {
                    const key = `${month}|${line}`;
                    const value = stored(month, line);
                    const shown =
                      edits[key] ??
                      (value === undefined
                        ? ''
                        : value.toLocaleString(intlLocale(locale), {
                            maximumFractionDigits: 2,
                          }));
                    return (
                      <td key={month} className="px-1 py-1">
                        <input
                          aria-label={`${t(`operations.planLine.${line}`)} ${formatMonthShort(month, locale)}`}
                          className="field-input w-24 px-2 py-1 text-right tabular-nums"
                          inputMode="decimal"
                          value={shown}
                          onChange={(e) =>
                            setEdits((s) => ({ ...s, [key]: e.target.value }))
                          }
                          onBlur={() => commit(month, line)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && e.currentTarget.blur()
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="field-hint">{t('operations.plan.hint')}</p>
      </section>
    </div>
  );
}

// ── Cash ────────────────────────────────────────────────────────────────────

function CashTab() {
  const { t, locale } = useLocale();
  const invalidate = useInvalidate();
  const balances = useQuery({
    queryKey: OPERATIONS_QUERY_KEYS.cash,
    queryFn: fetchCashBalances,
  });
  const [asOf, setAsOf] = useState(() => dayOfDate(new Date()));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createCashBalance,
    onSuccess: () => {
      setAmount('');
      setNote('');
      setError(null);
      invalidate(OPERATIONS_QUERY_KEYS.cash);
    },
    onError: (e) => setError(opsErrorMessage(e, t('operations.saveFailed'))),
  });
  const remove = useMutation({
    mutationFn: deleteCashBalance,
    onSuccess: () => invalidate(OPERATIONS_QUERY_KEYS.cash),
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    const value = parseAmount(amount);
    if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
      setError(t('operations.cash.invalid'));
      return;
    }
    create.mutate({ asOf, amountEur: value, note: note.trim() });
  }

  return (
    <div className="space-y-5">
      <StatusBanner tone="info">{t('operations.cash.lead')}</StatusBanner>
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      <section className="card">
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-4 items-end">
          <Field id="cashAsOf" label={t('operations.cash.asOf')}>
            <input
              id="cashAsOf"
              type="date"
              className="field-input"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </Field>
          <Field id="cashAmount" label={t('operations.cash.amount')}>
            <input
              id="cashAmount"
              className="field-input"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field id="cashNote" label={t('operations.cash.note')}>
            <input
              id="cashNote"
              className="field-input"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <div>
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={create.isPending}
            >
              {t('operations.cash.add')}
            </button>
          </div>
        </form>
      </section>
      <section className="surface p-5">
        {balances.data && balances.data.data.length === 0 && (
          <p className="text-sm text-gray-500">{t('operations.cash.empty')}</p>
        )}
        {balances.data && balances.data.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('operations.cash.asOf')}</th>
                  <th className="text-right">{t('operations.cash.amount')}</th>
                  <th>{t('operations.cash.note')}</th>
                  <th>{t('operations.costs.changedBy')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {balances.data.data.map((b, i) => (
                  <tr key={b.id}>
                    <td className="py-2">
                      {new Date(`${b.asOf}T12:00:00Z`).toLocaleDateString(
                        intlLocale(locale),
                      )}
                      {i === 0 && (
                        <span className="ml-2">
                          <Badge tone="teal">
                            {t('operations.cash.current')}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatMoney(b.amountEur, locale, { cents: true })}
                    </td>
                    <td className="text-gray-600">{b.note}</td>
                    <td className="text-xs text-gray-500">{b.createdBy}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="btn-neutral btn-sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(b.id)}
                      >
                        {t('operations.costs.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ── FX ──────────────────────────────────────────────────────────────────────

function FxTab() {
  const { t, locale } = useLocale();
  const invalidate = useInvalidate();
  const rates = useQuery({
    queryKey: OPERATIONS_QUERY_KEYS.fx,
    queryFn: fetchFxRates,
  });
  const [month, setMonth] = useState(() => monthOfDate(new Date()));
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: { month: string; rate: number }) =>
      setFxRate(input.month, input.rate),
    onSuccess: () => {
      setRate('');
      setError(null);
      invalidate(OPERATIONS_QUERY_KEYS.fx);
    },
    onError: (e) => setError(opsErrorMessage(e, t('operations.saveFailed'))),
  });
  const clear = useMutation({
    mutationFn: clearFxRate,
    onSuccess: () => invalidate(OPERATIONS_QUERY_KEYS.fx),
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    const value = Number(rate.replace(',', '.'));
    if (
      !isMonthKey(month) ||
      !Number.isFinite(value) ||
      value < 0.5 ||
      value > 2.5
    ) {
      setError(t('operations.fx.invalid'));
      return;
    }
    save.mutate({ month, rate: value });
  }

  return (
    <div className="space-y-5">
      <StatusBanner tone="info">{t('operations.fx.lead')}</StatusBanner>
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      <section className="card">
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-3 items-end">
          <Field id="fxMonth" label={t('operations.fx.month')}>
            <input
              id="fxMonth"
              type="month"
              className="field-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </Field>
          <Field
            id="fxRate"
            label={t('operations.fx.rate')}
            hint={t('operations.fx.rateHint')}
          >
            <input
              id="fxRate"
              className="field-input"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </Field>
          <div>
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={save.isPending}
            >
              {t('operations.fx.save')}
            </button>
          </div>
        </form>
      </section>
      <section className="surface p-5">
        {rates.data && rates.data.data.length === 0 && (
          <p className="text-sm text-gray-500">{t('operations.fx.empty')}</p>
        )}
        {rates.data && rates.data.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('operations.fx.month')}</th>
                  <th className="text-right">{t('operations.fx.rate')}</th>
                  <th>{t('operations.fx.source')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rates.data.data.map((r) => (
                  <tr key={r.month}>
                    <td className="py-2">
                      {formatMonthShort(r.month, locale)}
                    </td>
                    <td className="text-right tabular-nums">
                      {r.usdPerEur.toLocaleString(intlLocale(locale), {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 6,
                      })}
                    </td>
                    <td>
                      <Badge
                        tone={
                          r.source === OpsStoredFxSource.Manual
                            ? 'amber'
                            : 'gray'
                        }
                      >
                        {t(`operations.fx.sources.${r.source}`)}
                      </Badge>
                      {r.source === OpsStoredFxSource.Manual && (
                        <span className="ml-2 text-xs text-gray-500">
                          {r.setBy}
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="btn-neutral btn-sm"
                        disabled={clear.isPending}
                        onClick={() => clear.mutate(r.month)}
                      >
                        {r.source === OpsStoredFxSource.Manual
                          ? t('operations.fx.reset')
                          : t('operations.fx.refetch')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Partner API ─────────────────────────────────────────────────────────────

function PartnerTab() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: OPERATIONS_QUERY_KEYS.partner,
    queryFn: fetchPartnerStatus,
    refetchInterval: (q) => (q.state.data?.running ? 3_000 : 30_000),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const sync = useMutation({
    mutationFn: startPartnerSync,
    onSuccess: (started) => {
      setNotice(
        started
          ? t('operations.partner.started')
          : t('operations.partner.notStarted'),
      );
      void queryClient.invalidateQueries({
        queryKey: OPERATIONS_QUERY_KEYS.partner,
      });
      void queryClient.invalidateQueries({
        queryKey: OPERATIONS_QUERY_KEYS.report,
      });
    },
  });
  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(intlLocale(locale)) : '—';

  return (
    <div className="space-y-5">
      <StatusBanner tone="info">
        <ul className="list-disc pl-5 space-y-1">
          <li>{t('operations.partner.factSource')}</li>
          <li>{t('operations.partner.factSchedule')}</li>
          <li>{t('operations.partner.factAnalytics')}</li>
        </ul>
      </StatusBanner>
      {status.data && !status.data.configured && (
        <StatusBanner tone="warning">
          <p className="font-medium">{t('operations.partner.notConfigured')}</p>
          <ol className="list-decimal pl-5 mt-1 space-y-1">
            <li>{t('operations.partner.setup1')}</li>
            <li>{t('operations.partner.setup2')}</li>
            <li>{t('operations.partner.setup3')}</li>
          </ol>
        </StatusBanner>
      )}
      {notice && <StatusBanner tone="success">{notice}</StatusBanner>}
      {sync.isError && (
        <StatusBanner tone="error">
          {opsErrorMessage(sync.error, t('operations.saveFailed'))}
        </StatusBanner>
      )}
      <section className="surface p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900">
            {t('operations.partner.title')}
          </h2>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={
              !status.data?.configured || status.data.running || sync.isPending
            }
            onClick={() => sync.mutate()}
          >
            {status.data?.running
              ? t('operations.partner.running')
              : t('operations.partner.syncNow')}
          </button>
        </div>
        {status.data && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('operations.partner.stream')}</th>
                  <th>{t('operations.partner.lastSuccess')}</th>
                  <th>{t('operations.partner.syncedUntil')}</th>
                  <th>{t('operations.partner.oldest')}</th>
                  <th className="text-right">{t('operations.partner.rows')}</th>
                  <th>{t('operations.partner.error')}</th>
                </tr>
              </thead>
              <tbody>
                {status.data.streams.map((s) => (
                  <tr key={s.stream}>
                    <td className="py-2">
                      {t(`operations.partner.streams.${s.stream}`)}
                    </td>
                    <td>{when(s.lastSuccessAt)}</td>
                    <td>{when(s.syncedUntil)}</td>
                    <td>
                      {s.oldestSeenAt
                        ? new Date(s.oldestSeenAt).toLocaleDateString(
                            intlLocale(locale),
                          )
                        : '—'}
                    </td>
                    <td className="text-right tabular-nums">{s.rowsLastRun}</td>
                    <td className="text-red-600 text-xs max-w-sm">
                      {s.lastError ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
